import React from 'react';
import { Notification } from '../../types/notification';
import { getNotificationTitle, getNotificationMessage, hasCustomMessage } from '../../utils/notification/categoryMapper';
import { getNavigationPath, hasNavigationPath, UserType } from '../../utils/notification/navigation';
import { format } from 'date-fns';

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: number) => void;
  onNavigate?: (path: string) => void;
  userType: UserType;
  isForeigner?: boolean;
}

/**
 * 알림 아이템 컴포넌트
 *
 * @param notification 알림 객체
 * @param onMarkRead 읽음 처리 콜백
 * @param onNavigate 네비게이션 콜백 (선택적)
 * @param userType 사용자 타입
 * @param isForeigner 외국인 여부
 */
const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkRead,
  onNavigate,
  userType,
  isForeigner = false
}) => {
  const { id, category, title, titleEn, message, messageEn, metadata, isRead, createdAt } = notification;

  // 커스텀 메시지가 있는 카테고리인지 확인 (예: 전체공지)
  const isCustomMessage = hasCustomMessage(category);

  // 제목과 메시지 결정
  const displayTitle = isCustomMessage
    ? (isForeigner ? titleEn : title)
    : getNotificationTitle(category, metadata, isForeigner);

  const displayMessage = isCustomMessage
    ? (isForeigner ? messageEn : message)
    : getNotificationMessage(category, metadata, isForeigner);

  // 네비게이션 경로
  const navigationPath = getNavigationPath(notification, userType);
  const canNavigate = hasNavigationPath(notification, userType);

  // 날짜 포맷팅
  const formattedDate = createdAt ? format(new Date(createdAt), 'yyyy-MM-dd HH:mm') : '';

  /**
   * 알림 클릭 핸들러 (바로가기)
   */
  const handleClick = () => {
    if (canNavigate && navigationPath && onNavigate) {
      if (!isRead) {
        onMarkRead(id);
      }
      onNavigate(navigationPath);
    }
  };

  /**
   * 확인 버튼 클릭 핸들러
   */
  const handleMarkRead = (e: React.MouseEvent) => {
    e.stopPropagation(); // 부모 클릭 이벤트 방지
    onMarkRead(id);
  };

  return (
    <div
      className={`notification-item ${isRead ? 'read' : 'unread'} ${canNavigate ? 'clickable' : ''}`}
      onClick={canNavigate ? handleClick : undefined}
    >
      {!isRead && (
        <button className="notification-mark-read" onClick={handleMarkRead} title={isForeigner ? 'Mark as read' : '읽음 처리'}>
          ✓
        </button>
      )}
      <div className="notification-content">
        <div className="notification-header">
          <div className="notification-title">{displayTitle}</div>
          <div className="notification-time">{formattedDate}</div>
        </div>
        {displayMessage && (
          <div className="notification-message">
            {isCustomMessage ? (
              <div style={{ whiteSpace: 'pre-line' }}>{displayMessage}</div>
            ) : (
              displayMessage
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
